<template>
	<div v-if="visible" class="tutorial-checklist">
		<div class="tutorial-checklist__section">
			<button
				type="button"
				class="tutorial-checklist__head"
				@click="mainOpen = !mainOpen"
			>
				<span class="tutorial-checklist__title">
					Làm quen với Luca Education
				</span>
				<img
					v-if="mainOpen"
					src="@/assets/images/tutorial/chevron-up.svg"
					alt=""
					width="20"
					height="20"
				/>
				<img
					v-else
					src="@/assets/images/tutorial/chevron-down.svg"
					alt=""
					width="20"
					height="20"
				/>
			</button>
			<div class="tutorial-checklist__progress">
				<span class="tutorial-checklist__count">
					{{ mainProgress.done }}/{{ mainProgress.total || 5 }}
				</span>
				<div class="tutorial-checklist__bar">
					<div
						class="tutorial-checklist__bar-fill"
						:style="{ width: mainProgressPct + '%' }"
					></div>
				</div>
			</div>
			<template v-if="mainOpen">
				<div
					v-for="task in mainTasks"
					:key="task.id"
					class="tutorial-checklist__task"
					:class="{ 'is-active': isActiveTask(task) }"
					@click="onSelectTask(task)"
				>
					<img
						v-if="task.completed"
						src="@/assets/images/tutorial/check-circle-fill.svg"
						alt=""
						width="20"
						height="20"
					/>
					<img
						v-else
						src="@/assets/images/tutorial/radio.svg"
						alt=""
						width="20"
						height="20"
					/>
					<span class="tutorial-checklist__task-label">
						{{ taskDisplayTitle(task) }}
					</span>
					<img
						v-if="isActiveTask(task)"
						src="@/assets/images/tutorial/arrow-left.svg"
						alt=""
						width="16"
						height="16"
					/>
				</div>
			</template>
		</div>
		<div
			v-if="tuition"
			class="tutorial-checklist__section tutorial-checklist__section--bordered"
		>
			<button
				type="button"
				class="tutorial-checklist__head"
				@click="tuitionOpen = !tuitionOpen"
			>
				<span class="tutorial-checklist__title">
					{{ tuition.title || 'Khám phá Quản lý học phí' }}
				</span>
				<img
					v-if="tuitionOpen"
					src="@/assets/images/tutorial/chevron-up.svg"
					alt=""
					width="20"
					height="20"
				/>
				<img
					v-else
					src="@/assets/images/tutorial/chevron-down.svg"
					alt=""
					width="20"
					height="20"
				/>
			</button>
			<div class="tutorial-checklist__progress">
				<span class="tutorial-checklist__count">
					{{ tuitionProgress.done }}/{{ tuitionProgress.total || 2 }}
				</span>
				<div class="tutorial-checklist__bar">
					<div
						class="tutorial-checklist__bar-fill"
						:style="{ width: tuitionProgressPct + '%' }"
					></div>
				</div>
			</div>
			<template v-if="tuitionOpen">
				<div
					v-for="task in tuitionTasks"
					:key="task.id"
					class="tutorial-checklist__task"
					:class="{ 'is-active': isActiveTask(task) }"
					@click="onSelectTask(task)"
				>
					<img
						v-if="task.completed"
						src="@/assets/images/tutorial/check-circle-fill.svg"
						alt=""
						width="20"
						height="20"
					/>
					<img
						v-else
						src="@/assets/images/tutorial/radio.svg"
						alt=""
						width="20"
						height="20"
					/>
					<span class="tutorial-checklist__task-label">
						{{ taskDisplayTitle(task) }}
					</span>
					<img
						v-if="isActiveTask(task)"
						src="@/assets/images/tutorial/arrow-left.svg"
						alt=""
						width="16"
						height="16"
					/>
				</div>
			</template>
		</div>
		<div class="tutorial-checklist__footer">
			<button
				type="button"
				class="tutorial-checklist__later"
				@click="onLater"
			>
				Để sau
			</button>
		</div>
	</div>
</template>

<script>
import { computed, defineComponent, ref } from 'vue';
import { useTutorial } from '@/features/tutorial/useTutorial';

export default defineComponent({
	name: 'TutorialChecklist',
	emits: ['later'],
	setup(_, { emit }) {
		const {
			canAccessTutorial,
			phase,
			checklistExpanded,
			gettingStarted,
			tuitionTutorial,
			activeTask,
			startTask,
			taskDisplayTitle,
			sortedTasks,
			progress,
		} = useTutorial();

		const mainOpen = ref(true);
		const tuitionOpen = ref(false);

		const visible = computed(
			() =>
				canAccessTutorial.value &&
				checklistExpanded.value &&
				(phase.value === 'tour' ||
					phase.value === 'step_success' ||
					phase.value === 'paused')
		);

		const mainTasks = computed(() => sortedTasks(gettingStarted.value));
		const tuitionTasks = computed(() => sortedTasks(tuitionTutorial.value));
		const mainProgress = computed(() => progress(gettingStarted.value));
		const mainProgressPct = computed(() => {
			const { done, total } = mainProgress.value;
			if (!total) return 0;
			return Math.round((done / total) * 100);
		});

		const tuition = computed(() => tuitionTutorial.value);
		const tuitionProgress = computed(() => progress(tuition.value));
		const tuitionProgressPct = computed(() => {
			const { done, total } = tuitionProgress.value;
			if (!total) return 0;
			return Math.round((done / total) * 100);
		});

		function isActiveTask(task) {
			return activeTask.value?.id === task.id;
		}

		function onSelectTask(task) {
			startTask(task);
		}

		function onLater() {
			emit('later');
		}

		return {
			visible,
			mainOpen,
			tuitionOpen,
			mainTasks,
			tuitionTasks,
			mainProgress,
			mainProgressPct,
			tuition,
			tuitionProgress,
			tuitionProgressPct,
			taskDisplayTitle,
			isActiveTask,
			onSelectTask,
			onLater,
		};
	},
});
</script>

<style scoped>
.tutorial-checklist {
	position: fixed;
	right: 24px;
	bottom: 81px;
	z-index: 40;
	width: 320px;
	max-width: calc(100vw - 48px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
	border-radius: 8px;
	background: #fff;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24),
		0 3px 8px -1px rgba(50, 50, 71, 0.05);
}
.tutorial-checklist__section {
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 16px;
}
.tutorial-checklist__section--bordered {
	border-top: 1px solid #e1e7ef;
	border-bottom: 1px solid #e1e7ef;
}
.tutorial-checklist__head {
	display: flex;
	align-items: flex-start;
	gap: 16px;
	width: 100%;
	text-align: left;
}
.tutorial-checklist__title {
	flex: 1;
	min-width: 0;
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
	color: #48566a;
}
.tutorial-checklist__progress {
	display: flex;
	align-items: center;
	gap: 8px;
	height: 24px;
}
.tutorial-checklist__count {
	flex-shrink: 0;
	font-size: 16px;
	line-height: 24px;
	font-weight: 600;
	color: #48566a;
}
.tutorial-checklist__bar {
	flex: 1;
	min-width: 0;
	height: 10px;
	border-radius: 5px;
	background: #f1f5f9;
	overflow: hidden;
}
.tutorial-checklist__bar-fill {
	height: 100%;
	border-radius: 5px;
	background: #e5a42b;
	transition: width 0.2s ease;
}
.tutorial-checklist__task {
	display: flex;
	align-items: center;
	gap: 6px;
	min-height: 20px;
	cursor: pointer;
	font-size: 14px;
	line-height: 20px;
	color: #344256;
}
.tutorial-checklist__task img {
	flex-shrink: 0;
}
.tutorial-checklist__task-label {
	flex: 1;
	min-width: 0;
}
.tutorial-checklist__task.is-active .tutorial-checklist__task-label {
	flex: 0 1 auto;
	font-weight: 600;
}
.tutorial-checklist__footer {
	display: flex;
	justify-content: center;
	padding: 0 16px;
	background: #f8fafc;
}
.tutorial-checklist__later {
	width: 240px;
	padding: 12px 18px;
	border-radius: 5px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
	color: #65758b;
}
</style>
